import { Dispatch, SetStateAction, useContext, useMemo, useState } from 'react';
import { LegendItem, LegendLabel, LegendOrdinal, LegendShape } from '@visx/legend';
import * as d3Color from '@visx/vendor/d3-color';
import { DataContext } from '@visx/xychart';
import { keyBy } from 'lodash';

import { GroupConfig, StudioVisualizationType } from '@blockworks/platform/api/research/studio';
import { useTrack } from '@blockworks/platform/services';
import { FlexBox, Input, MagnifyingGlassIcon, Text } from '@blockworks/ui';
import { Bit } from '@blockworks/ui/factory';
import { cn } from '@blockworks/ui/system';

import { AnalyticsEvent } from '../../../../../utils/events-tracking';
import {
    containerProps,
    GetVisualizationPropsResponse,
    internalContainerProps,
    LEGEND_HTML_ID,
    orientationMap,
    positonProps,
    wrapperProps,
} from '../../../models';

const SeriesLabel = ({ series, isFaded }: { isFaded?: boolean; series?: GroupConfig<any>['series'][number] }) => {
    if (!series) return null;
    const { label } = series;
    const labelColor = isFaded ? 'deselect' : 'base';

    return (
        <Text color={labelColor} size="xxs" wordBreak="all" lines={1}>
            {label}
        </Text>
    );
};

type LegendProps<T> = {
    position?: 'left' | 'right' | 'bottom' | 'top';
    groups: GetVisualizationPropsResponse<StudioVisualizationType.Timeseries>['options']['groups'];
    onHighlight?: (column: keyof T | undefined) => void;
    highlight: keyof T | undefined;
    onHide?: Dispatch<SetStateAction<Array<keyof T>>>;
    hidden: Array<keyof T>;
    isSearchable?: boolean;
    disableAll?: boolean;
};

function Legend<T>({
    isSearchable,
    groups,
    position = 'bottom',
    onHighlight,
    highlight,
    onHide,
    hidden,
}: LegendProps<T>) {
    const orientation = orientationMap[position];
    const track = useTrack();
    const [search, setSearch] = useState('');

    const seriesByKey = useMemo(
        () =>
            groups
                .flatMap(({ series }) => series)
                .reduce<Record<string, GroupConfig<any>['series'][number]>>((acc, series) => {
                    acc[series.column as string] = { ...series, label: series.label || series.column };
                    return acc;
                }, {}),
        [groups],
    );

    const { colorScale } = useContext(DataContext);
    if (!colorScale) return null;
    return (
        <Bit.div {...wrapperProps[orientation]} {...positonProps[position]} id={LEGEND_HTML_ID}>
            <Bit.div className={internalContainerProps[orientation]}>
                <LegendOrdinal scale={colorScale}>
                    {labels => {
                        const labelByKey = keyBy(labels, label => label.text);
                        const seriesList = Object.entries(seriesByKey);
                        const allSeriesIds = seriesList.map(([id]) => id) as typeof hidden;
                        const hasMultipleSeries = seriesList.length > 1;

                        return (
                            <FlexBox py={orientation === 'vertical' ? 1 : 0} {...containerProps[orientation]}>
                                {hasMultipleSeries && (
                                    <LegendItem
                                        className="gap-.125 flex cursor-pointer"
                                        onClick={() => {
                                            track?.(AnalyticsEvent.LegendItemClicked, { item: 'All', groups });
                                            if (hidden.length === 0) {
                                                onHide?.(allSeriesIds);
                                            } else {
                                                onHide?.([]);
                                            }
                                        }}
                                    >
                                        <LegendShape
                                            shape="circle"
                                            label={labels[0]!}
                                            itemIndex={0}
                                            height={8}
                                            width={8}
                                            margin={6}
                                            item={'All'}
                                            fill={() => 'var(--color-content)'}
                                        />
                                        <LegendLabel margin={0} label={<Text size="xxs">All</Text>} align="left" />
                                    </LegendItem>
                                )}
                                {seriesList.map(([id, seriesConfig], i) => {
                                    const seriesId = id as keyof T;
                                    const label = labelByKey[id];
                                    if (!label) return null;
                                    const searchableFields = [seriesConfig.label, seriesConfig.column].map(value =>
                                        value.toLowerCase(),
                                    );
                                    const isVisible = search
                                        ? searchableFields.some(value => value.includes(search.toLowerCase()))
                                        : true;
                                    if (!isVisible) return null;
                                    const isHidden = hidden.includes(seriesId);
                                    const isFaded = (highlight && highlight !== id) || isHidden;

                                    return (
                                        <LegendItem
                                            key={`legend-quantile-${id}`}
                                            className=" gap-.125 flex cursor-pointer"
                                            onMouseOver={() => onHighlight?.(seriesId)}
                                            onMouseOut={() => onHighlight?.(undefined)}
                                            onClick={() => {
                                                track?.(AnalyticsEvent.LegendItemClicked, { item: id, groups });

                                                if (!hasMultipleSeries && !isSearchable) return;
                                                setSearch('');
                                                onHide?.(prev => {
                                                    if (isHidden) {
                                                        const hiddenItems = prev.filter(item => item !== id);
                                                        return hiddenItems;
                                                    }
                                                    return [...prev, seriesId];
                                                });
                                            }}
                                        >
                                            <LegendShape
                                                shape="circle"
                                                itemIndex={i}
                                                label={label}
                                                height={8}
                                                width={8}
                                                margin={6}
                                                item={id}
                                                fill={() => {
                                                    const color = seriesConfig.color;
                                                    const baseColor = d3Color.color(color);
                                                    const opacity = isHidden ? 0.25 : 1;
                                                    return baseColor?.copy({ opacity }).formatRgb();
                                                }}
                                            />
                                            <LegendLabel
                                                margin={0}
                                                label={<SeriesLabel isFaded={isFaded} series={seriesConfig} />}
                                                align="left"
                                                className={cn({ 'py-1': orientation === 'vertical' })}
                                            />
                                        </LegendItem>
                                    );
                                })}
                            </FlexBox>
                        );
                    }}
                </LegendOrdinal>
            </Bit.div>
            {isSearchable && (
                <FlexBox borderTop={1}>
                    <Input
                        classNames={{
                            base: 'focus-within:ring-0',
                            icon: 'text-surface-accent h-4 w-4',
                            input: 'text-xxs placeholder:text-surface-accent bg-surface rounded-md pl-6 py-1',
                        }}
                        icon={MagnifyingGlassIcon}
                        onChange={e => setSearch(e.target.value)}
                        name="search"
                        placeholder="Search"
                        size="sm"
                        w="full"
                        variant="transparent"
                    />
                </FlexBox>
            )}
        </Bit.div>
    );
}

export { Legend };
